import { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import { AlertCircle, BellRing, Check, Loader2 } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import {
  addWakeBackup,
  getWhoWeWake,
  removeWakeBackup,
  resendWakeBackupCode,
  verifyWakeBackup,
  type WakePerson,
} from '../lib/whoWeWake'

/**
 * "Who we wake" (BRIEF-A10): the owner first, from their verified mobile,
 * then at most one backup person. The backup's mobile is checked with a
 * code texted to it and entered here; until then we never text them about
 * an escalation. Only the owner sees this section (the backend refuses
 * anyone else with 403). Numbers are shown by their last three digits.
 */

function detailOf(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)?.detail
    if (typeof detail === 'string' && detail) return detail
  }
  return fallback
}

function endingText(person: WakePerson): string {
  return person.ending ? `Mobile ending ${person.ending}` : 'No mobile yet'
}

export function WhoWeWakeSection() {
  const isOwner = useAuthStore((s) => s.principal?.org_role === 'owner')
  const [people, setPeople] = useState<WakePerson[] | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoadError(false)
    try {
      setPeople((await getWhoWeWake()).people)
    } catch {
      setLoadError(true)
    }
  }, [])

  useEffect(() => {
    if (isOwner) void load()
  }, [isOwner, load])

  const run = useCallback(
    async (
      step: () => Promise<{ people: WakePerson[] }>,
      fallback: string,
      done: string | null = null,
    ) => {
      if (busy) return
      setBusy(true)
      setError(null)
      setNotice(null)
      try {
        setPeople((await step()).people)
        setCode('')
        setNotice(done)
      } catch (err) {
        setError(detailOf(err, fallback))
        // The backup may be saved even though the code was not sent (too
        // many codes this hour, a 429): show the list as it now stands, so
        // the code step and "Send a new code" appear.
        try {
          setPeople((await getWhoWeWake()).people)
        } catch {
          // keep what is shown
        }
      } finally {
        setBusy(false)
      }
    },
    [busy],
  )

  if (!isOwner) return null

  const owner = people?.find((p) => p.role === 'owner')
  const backup = people?.find((p) => p.role === 'backup')

  return (
    <section
      aria-labelledby="who-we-wake-heading"
      className="mt-5 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
    >
      <div className="flex items-center gap-2">
        <BellRing className="w-4 h-4 text-brass" aria-hidden="true" />
        <h2 id="who-we-wake-heading" className="text-sm font-semibold text-navy">
          Who we wake
        </h2>
      </div>
      <p className="mt-0.5 text-xs text-gray-500">
        If something serious happens, we text you first. If you might be on a
        plane, add one backup person we can ask to get hold of you.
      </p>

      {loadError ? (
        <div className="mt-3 flex items-start gap-2 text-sm text-danger" role="alert">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
          <span>We could not load this just now.</span>
          <button
            type="button"
            onClick={() => void load()}
            className="ml-auto text-xs font-medium underline"
          >
            Try again
          </button>
        </div>
      ) : people === null ? (
        <div className="mt-3 flex items-center text-gray-400" role="status">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          <span className="ml-2 text-xs">Loading...</span>
        </div>
      ) : (
        <ol className="mt-3 space-y-2">
          <li className="rounded-xl border border-gray-200 bg-cream p-3">
            <p className="text-sm font-medium text-navy">
              1. {owner?.name || 'You'} (you)
            </p>
            <p className="text-xs text-gray-500">
              {owner ? endingText(owner) : 'No mobile yet'}
              {owner && !owner.verified && ', not checked yet'}
            </p>
          </li>

          {backup ? (
            <li className="rounded-xl border border-gray-200 p-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-medium text-navy">
                    2. {backup.name} (backup)
                  </p>
                  <p className="text-xs text-gray-500">{endingText(backup)}</p>
                  {backup.verified ? (
                    <p className="mt-1 inline-flex items-center gap-1 text-xs text-success">
                      <Check className="w-3.5 h-3.5" aria-hidden="true" />
                      Checked
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-600">
                      Not checked yet. We will not text them until you enter
                      the code we sent to their mobile.
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    void run(removeWakeBackup, 'We could not remove them just now.')
                  }
                  className="tap-min shrink-0 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-navy hover:border-brass disabled:opacity-50"
                >
                  Remove
                </button>
              </div>

              {!backup.verified && (
                <div className="mt-3">
                  <form
                    className="flex items-end gap-2"
                    onSubmit={(e) => {
                      e.preventDefault()
                      void run(
                        () => verifyWakeBackup(code.trim()),
                        'That code did not work. Please check it and try again.',
                      )
                    }}
                  >
                    <div className="min-w-0 flex-1">
                      <label
                        htmlFor="wake-backup-code"
                        className="block text-xs font-medium text-navy"
                      >
                        Code from their text
                      </label>
                      <input
                        id="wake-backup-code"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                        className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-brass/40"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={busy || code.length !== 6}
                      className="tap-min rounded-lg bg-brass px-3.5 py-2 text-sm font-medium text-white hover:bg-brass-dark disabled:opacity-50"
                    >
                      Check code
                    </button>
                  </form>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      void run(
                        resendWakeBackupCode,
                        'We could not send a new code just now.',
                        'We have sent a new code to their mobile.',
                      )
                    }
                    className="tap-min mt-2 text-xs font-medium text-navy underline disabled:opacity-50"
                  >
                    Send a new code
                  </button>
                </div>
              )}
            </li>
          ) : (
            <li className="rounded-xl border border-dashed border-gray-300 p-3">
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  void run(
                    () => addWakeBackup(name.trim(), phone.trim()),
                    'We could not add them just now. Please try again.',
                  )
                }}
                className="space-y-2"
              >
                <p className="text-sm font-medium text-navy">Add a backup person</p>
                <div>
                  <label
                    htmlFor="wake-backup-name"
                    className="block text-xs font-medium text-navy"
                  >
                    Their name
                  </label>
                  <input
                    id="wake-backup-name"
                    type="text"
                    maxLength={60}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-brass/40"
                  />
                </div>
                <div>
                  <label
                    htmlFor="wake-backup-phone"
                    className="block text-xs font-medium text-navy"
                  >
                    Their mobile
                  </label>
                  <input
                    id="wake-backup-phone"
                    type="tel"
                    inputMode="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="021 123 4567"
                    className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-navy placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brass/40"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    We will text them a code. Ask them for it and enter it here.
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={busy || !name.trim() || !phone.trim()}
                  className="tap-min rounded-lg bg-brass px-3.5 py-2 text-sm font-medium text-white hover:bg-brass-dark disabled:opacity-50"
                >
                  Add and send code
                </button>
              </form>
            </li>
          )}
        </ol>
      )}

      {notice && !error && (
        <p className="mt-2 text-xs text-success" role="status">
          {notice}
        </p>
      )}

      {error && (
        <p className="mt-2 text-xs text-danger" role="alert">
          {error}
        </p>
      )}
    </section>
  )
}
